// src/pages/Dashboard/components/DashboardProducts.jsx
import React, { useEffect, useState } from "react";
import { Card, Segmented, Modal, Spin, Empty, Grid } from "antd";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { postData } from "../../../apiservice/apis"; // ✅ adjust import path
import ChartTooltip from "./ChartTooltip";
import { CATEGORICAL, INK, axisTick } from "../dashboardTheme";

const { useBreakpoint } = Grid;

// Colour by rank; anything past the palette folds into a neutral grey.
const productColor = (index) => CATEGORICAL[index] || INK.axis;

const DashboardProducts = ({ fromDate, toDate, currency }) => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [mostSellingData, setMostSellingData] = useState([]);
  const [leastSellingData, setLeastSellingData] = useState([]);
  const [activeTab, setActiveTab] = useState("best");
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDetails, setProductDetails] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const fetchProducts = async (mode) => {
    try {
      setLoading(true);
      if (mode === "least") {
        setLeastSellingData([]);
      } else {
        setMostSellingData([]);
      }
      const url =
        mode === "least"
          ? "/dashboard/least-selling-products"
          : "/dashboard/best-selling-products";

      const response = await postData(url, { fromDate, toDate });
      const products = response.data.selling_products || [];
      const data = products.map((p) => ({
        name: p.item_name,
        value: Number(Number(p["SUM(sd.item_total)"] || 0).toFixed(2)
      ),
        item_id: p.item_id,
      }));
      if (mode === "least") {
        setLeastSellingData(data);
      } else {
        setMostSellingData(data);
      }
    } catch (err) {
      console.error("Error fetching products:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProductDetails = async (itemId) => {
    try {
      setDetailsLoading(true);
      const res = await postData("/dashboard/product-sales-history", {
        item_id: itemId,
        fromDate,
        toDate,
      });

      const data = (res.data || []).map((d) => ({
        date: d.date,
        qty: Number(d.qty || 0),
        amount: Number(d.amount || 0),
      }));
      setProductDetails(data);
    } catch (err) {
      console.error("Error fetching product details:", err);
      setProductDetails([]);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setSelectedProduct(null);
    fetchProducts(key);
  };

  const handleSliceClick = (product, index) => {
    setSelectedProduct({ ...product, color: productColor(index) });
    fetchProductDetails(product.item_id);
  };

  useEffect(() => {
    fetchProducts("best"); // default tab
    setActiveTab("best");
  }, [fromDate, toDate]);

  const data = activeTab === "least" ? leastSellingData : mostSellingData;
  const total = data.reduce((s, p) => s + p.value, 0);

  return (
    <Card className="erp-card" variant="borderless">
      <div className="erp-card-head">
        <div>
          <div className="erp-card-title">
            {activeTab === "least" ? "Least Selling Products" : "Most Selling Products"}
          </div>
          <div className="erp-card-sub">Sales value · click a product for details</div>
        </div>
        <Segmented
          className="erp-seg"
          size="small"
          value={activeTab}
          onChange={handleTabChange}
          options={[
            { label: isMobile ? "Most" : "Most Selling", value: "best" },
            { label: isMobile ? "Least" : "Least Selling", value: "least" },
          ]}
        />
      </div>

      {loading ? (
        <div className="erp-loading" style={{ height: 168 }}>
          <Spin />
        </div>
      ) : !data.length ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No product sales in this period" className="erp-empty" />
      ) : (
        <div className="erp-donut">
          <div className="erp-donut-chart">
            <ResponsiveContainer width="100%" height={168}>
              <PieChart>
                <Pie
                  data={data}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={56}
                  outerRadius={80}
                  paddingAngle={2}
                  stroke="none"
                  onClick={(entry, index) => handleSliceClick(data[index], index)}
                  style={{ cursor: "pointer" }}
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={productColor(index)} />
                  ))}
                </Pie>
                <Tooltip content={<ChartTooltip currency={currency} />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="erp-donut-center">
              <b title={`${total.toFixed(2)} ${currency}`}>{total.toFixed(2)}</b>
              <span>Total {currency}</span>
            </div>
          </div>

          <ul className="erp-list scroll">
            {data.map((p, index) => (
              <li
                key={p.item_id ?? p.name}
                className={`clickable${selectedProduct?.item_id === p.item_id ? " active" : ""}`}
                onClick={() => handleSliceClick(p, index)}
                title={p.name}
              >
                <i style={{ background: productColor(index) }} />
                <span className="name">{p.name}</span>
                <b>
                  {p.value.toFixed(2)} <span className="unit">{currency}</span>
                </b>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Product Details Modal */}
      <Modal
        open={!!selectedProduct}
        title={`Details for ${selectedProduct?.name}`}
        footer={null}
        onCancel={() => {
          setSelectedProduct(null);
          setProductDetails([]);
        }}
        width={700}
      >
        {detailsLoading ? (
          <Spin />
        ) : productDetails.length > 0 ? (
          <>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={productDetails}>
                <CartesianGrid stroke={INK.grid} strokeOpacity={0.7} vertical={false} />
                <XAxis dataKey="date" tickLine={false} axisLine={{ stroke: INK.axis }} tick={axisTick} />
                <YAxis tickLine={false} axisLine={false} tick={axisTick} />
                <Tooltip formatter={(val) => `${val} ${currency}`} />
                <Line type="monotone" dataKey="amount" stroke={selectedProduct?.color} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>

            <ul style={{ marginTop: "1rem" }}>
              {productDetails.map((d, i) => (
                <li key={i}>
                  <strong>{d.date}</strong> – {d.qty} units – {d.amount.toFixed(2)} {currency}
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p>No sales data found for this product.</p>
        )}
      </Modal>
    </Card>
  );
};

export default DashboardProducts;
